import React, { useState, useEffect } from "react";
import { Dropdown, Popconfirm, Input, Button } from "antd";
import { DeleteOutlined, EditOutlined, MoreOutlined, UpOutlined, DownOutlined } from "@ant-design/icons";
import { format, fromUnixTime } from "date-fns";
import { CommentAvatar } from "utils/constants";
import { editComment, deleteComment, postComment } from "services/comments";
import { getUser } from "services/user";
import getUnixTime from "date-fns/getUnixTime";
import { useDispatch, useSelector } from "react-redux";
import { actions as profileActions } from "modules/profile";
import { actions as prospectProfileActions } from "modules/prospectProfile";
import { getParentComments } from "services/klingKlong";
import { actions as notificationsActions } from "modules/notifications";

const getDescendantCount = (comments, parentCid) => {
  const children = comments.filter((c) => c.pid === parentCid);
  return children.reduce((count, child) => count + 1 + getDescendantCount(comments, child.cid), 0);
};

const CommentRenderer = ({
  comment,
  comments,
  userId,
  onReply,
  setReplyPopoverCid,
  replyPopoverCid,
  newCommentBody,
  setNewCommentBody,
  handleReply,
  onEdit,
  setEditPopoverCid,
  editPopoverCid,
  handleEditConfirm,
  onDelete,
  setDeletePopoverCid,
  deleteDateLoading,
  expandedReplies,
  toggleReplies,
}) => {
  const { cid, user, body, created } = comment;
  const children = comments.filter((c) => c.pid === cid);
  const nestedRepliesCount = getDescendantCount(comments, cid);
  const isExpanded = expandedReplies.has(cid);

  const menuItems = [
    {
      key: "edit",
      label: (
        <div style={{ display: "flex", alignItems: "center" }} onClick={() => onEdit(cid, "edit", body)}>
          <EditOutlined style={{ marginRight: 8 }} />
          Edit
        </div>
      ),
    },
    {
      key: "delete",
      label: (
        <Popconfirm
          title={
            <div>
              Are you sure you want to delete this comment? <br /> By deleting this comment, you will also delete all of
              its replies.
            </div>
          }
          onConfirm={onDelete}
          onCancel={() => setDeletePopoverCid(null)}
          okText="Delete"
          cancelText="Cancel"
          okButtonProps={{ loading: deleteDateLoading }}
        >
          <div
            style={{ display: "flex", alignItems: "center", color: "red", cursor: "pointer" }}
            onClick={() => setDeletePopoverCid(cid)}
          >
            <DeleteOutlined style={{ marginRight: 8 }} />
            Delete
          </div>
        </Popconfirm>
      ),
    },
  ];

  return (
    <div className="comment-row" id={`comment-${cid}`} style={{ width: "100%" }}>
      <div className="comment-body-wrapper">
        <div style={{ display: "flex", gap: 8 }}>
          <img src={CommentAvatar} alt="avatar" style={{ width: 30, height: 30, borderRadius: "50%" }} />

          <div style={{ flex: 1 }}>
            <div style={{ marginBottom: 4 }}>
              <span style={{ fontWeight: 600, marginRight: 8 }}>{user?.name}</span>
              <span style={{ color: "#999", fontSize: "0.85em" }}>
                {format(new Date(fromUnixTime(created)), "dd MMM yyyy, HH:mm")}
              </span>
            </div>
            {editPopoverCid === cid ? (
              <div>
                <Input.TextArea
                  value={newCommentBody}
                  onChange={(e) => setNewCommentBody(e.target.value)}
                  autoSize={{ minRows: 1, maxRows: 6 }}
                  style={{
                    border: "none",
                    borderBottom: "1px solid #b99022",
                    outline: "none",
                    boxShadow: "none",
                  }}
                />
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16, gap: 8 }}>
                  <Button
                    onClick={() => {
                      setEditPopoverCid(null);
                      setNewCommentBody("");
                    }}
                  >
                    Cancel
                  </Button>
                  <Button onClick={() => handleEditConfirm()} type="primary">
                    Save
                  </Button>
                </div>
              </div>
            ) : body === undefined ? (
              body
            ) : (
              <pre>{body}</pre>
            )}
          </div>
        </div>
        <div className="comment-reply-link" onClick={() => setReplyPopoverCid(cid)}>
          Reply
        </div>
        {replyPopoverCid !== null && replyPopoverCid === cid && (
          //custom modal for replying to comments, since antd popover doesn't support controlled inputs
          <div>
            <Input.TextArea
              value={newCommentBody}
              onChange={(e) => setNewCommentBody(e.target.value)}
              autoSize={{ minRows: 1, maxRows: 6 }}
              style={{
                border: "none",
                borderBottom: "1px solid #b99022",
                outline: "none",
                boxShadow: "none",
              }}
            ></Input.TextArea>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16, gap: 8 }}>
              <Button
                onClick={() => {
                  setReplyPopoverCid(null);
                  setNewCommentBody("");
                }}
              >
                Cancel
              </Button>
              <Button onClick={() => handleReply(replyPopoverCid)} type="primary">
                Save
              </Button>
            </div>
          </div>
        )}
        {children.length > 0 && (
          <div className="comment-children-wrapper">
            <button className="comment-replies-toggle" onClick={() => toggleReplies(cid)}>
              {` (${nestedRepliesCount}) Replies`}
              {isExpanded ? (
                <UpOutlined style={{ marginLeft: "6px" }} />
              ) : (
                <DownOutlined style={{ marginLeft: "6px" }} />
              )}
            </button>
            {isExpanded && (
              <div style={{ border: "1px solid #f0f0f0", padding: 10, borderRadius: 4, marginBottom: 5 }}>
                {children.map((childComment) => (
                  <CommentRenderer
                    key={childComment.cid}
                    comment={childComment}
                    comments={comments}
                    userId={userId}
                    onReply={onReply}
                    setReplyPopoverCid={setReplyPopoverCid}
                    replyPopoverCid={replyPopoverCid}
                    newCommentBody={newCommentBody}
                    setNewCommentBody={setNewCommentBody}
                    handleReply={handleReply}
                    onEdit={onEdit}
                    setEditPopoverCid={setEditPopoverCid}
                    editPopoverCid={editPopoverCid}
                    handleEditConfirm={handleEditConfirm}
                    onDelete={onDelete}
                    setDeletePopoverCid={setDeletePopoverCid}
                    deleteDateLoading={deleteDateLoading}
                    expandedReplies={expandedReplies}
                    toggleReplies={toggleReplies}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {userId && Number(userId) === user?.id && (
        <Dropdown menu={{ items: menuItems }} placement="bottomRight" trigger={["click"]}>
          <MoreOutlined className="comment-actions-trigger" />
        </Dropdown>
      )}
    </div>
  );
};

export const CommentList = ({ comments, commentType, setCommentsUI, parentId }) => {
  const profileId = useSelector((state) =>
    commentType === "comment_node_profile"
      ? state?.profile?.profileInfo?.id
      : state?.prospectProfile?.prospectProfileInfo?.id
  );
  const dispatch = useDispatch();
  const [userId, setUserId] = useState(null);
  const [replyPopoverCid, setReplyPopoverCid] = useState(null);
  const [editPopoverCid, setEditPopoverCid] = useState(null);
  const [deletePopoverCid, setDeletePopoverCid] = useState(null);
  const [deleteDateLoading, setDeleteDateLoading] = useState(false);
  const [expandedReplies, setExpandedReplies] = useState(new Set());
  const [newCommentBody, setNewCommentBody] = useState("");
  const firstName = useSelector((state) => state?.user?.firstName);
  const lastName = useSelector((state) => state?.user?.lastName);

  const toggleReplies = (cid) => {
    setExpandedReplies((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(cid)) {
        newSet.delete(cid);
      } else {
        newSet.add(cid);
      }
      return newSet;
    });
  };

  useEffect(() => {
    if (!userId) {
      getUser().then((response) => {
        if (response.status === 200) {
          setUserId(response.data.user_id);
        }
      });
    }
  }, [comments]);

  useEffect(() => {
    // Extract cid from URL query parameters
    const searchParams = new URLSearchParams(window.location.search);
    const targetCid = searchParams.get("cid");

    if (targetCid && comments.length > 0) {
      // Find all parent comments that need to be expanded to show the target comment
      const commentMap = new Map(comments.map((c) => [String(c.cid), c]));
      const targetCommentData = commentMap.get(String(targetCid));

      if (targetCommentData) {
        const toExpand = new Set();
        let currentPid = targetCommentData.pid;

        // Traverse up the parent chain and collect all parents
        while (currentPid) {
          toExpand.add(currentPid);
          const parentComment = commentMap.get(String(currentPid));
          if (!parentComment) break;
          currentPid = parentComment.pid;
        }

        // Expand all parent comments
        if (toExpand.size > 0) {
          setExpandedReplies(toExpand);
        }

        // Scroll to the target comment after a short delay to ensure DOM is updated
        setTimeout(() => {
          const element = document.getElementById(`comment-${targetCid}`);
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "center" });
            // Optional: highlight the comment
            element.style.backgroundColor = "#fffbe6";
            setTimeout(() => {
              element.style.backgroundColor = "";
            }, 2000);
          }
        }, 100);
      }
    }
  }, [comments]);

  const handleReply = (cid) => {
    if (commentType === "kk_parent_comment") {
      const data = {
        entity_type: [{ value: "node" }],
        entity_id: [{ target_id: parentId }],
        comment_body: [{ value: newCommentBody }],
        comment_type: [{ target_id: "kk_parent_comment" }],
        field_name: [{ value: "comment" }],
        pid: [{ target_id: cid }],
      };

      postComment(data)
        .then(() => {
          getParentComments(parentId).then((data) => {
            setCommentsUI(data.data);
          });
        })
        .catch((err) => console.log(err));
      setNewCommentBody("");
      setReplyPopoverCid(null);
      dispatch(notificationsActions.getNotificationsInfo());
    } else {
      const data = {
        entity_type: [{ value: "node" }],
        entity_id: [{ target_id: profileId }],
        comment_body: [{ value: newCommentBody }],
        comment_type: [{ target_id: commentType }],
        field_name: [{ value: commentType }],
        pid: [{ target_id: cid }],
      };

      postComment(data)
        .then(() => {
          setCommentsUI([
            {
              user: { name: `${firstName} ${lastName}` },
              body: newCommentBody,
              created: getUnixTime(new Date()),
            },
            ...comments,
          ]);
          if (commentType === "comment_node_profile") {
            dispatch(profileActions.getProfile(profileId));
          } else {
            dispatch(prospectProfileActions.getProspectProfile(profileId));
          }
        })
        .catch((err) => console.log(err));
      setNewCommentBody("");
      setReplyPopoverCid(null);
      dispatch(notificationsActions.getNotificationsInfo());
    }
  };

  const handleMenuClick = async (cid, key, body) => {
    if (key === "edit") {
      setEditPopoverCid(cid);
      setNewCommentBody(body);
    } else if (key === "delete") {
      setDeletePopoverCid(cid);
    }
  };

  const handleEditConfirm = async () => {
    if (editPopoverCid) {
      editComment(editPopoverCid, { comment_body: newCommentBody, comment_type: [{ target_id: commentType }] }).then(
        (res) => {
          if (res) {
            setEditPopoverCid(null);
            setNewCommentBody("");
            if (commentType === "comment_node_prospect_student") {
              //remember to change this to getComments when the end point is ready and set the commentsUI instead of dispatching the whole profile again
              dispatch(prospectProfileActions.getProspectProfile(profileId));
            } else if (commentType === "comment_node_profile") {
              //remember to change this to getComments when the end point is ready and set the commentsUI instead of dispatching the whole profile again
              dispatch(profileActions.getProfile(profileId));
            } else if (commentType === "kk_parent_comment") {
              getParentComments(parentId).then((data) => {
                setCommentsUI(data.data);
              });
            }
          }
        }
      );
    }
  };

  const handleDeleteConfirm = async () => {
    if (deletePopoverCid) {
      setDeleteDateLoading(true);
      deleteComment(deletePopoverCid).then((res) => {
        if (res) {
          setDeleteDateLoading(false);
          setDeletePopoverCid(null);
          if (commentType === "comment_node_prospect_student") {
            //remember to change this to getComments when the end point is ready and set the commentsUI instead of dispatching the whole profile again
            dispatch(prospectProfileActions.getProspectProfile(profileId));
          } else if (commentType === "comment_node_profile") {
            //remember to change this to getComments when the end point is ready and set the commentsUI instead of dispatching the whole profile again
            dispatch(profileActions.getProfile(profileId));
          } else if (commentType === "kk_parent_comment") {
            getParentComments(parentId).then((data) => {
              setCommentsUI(data.data);
            });
          }
        }
      });
    }
  };

  const parentComments = comments.filter((c) => c.pid === null);

  return (
    <>
      <div style={{ marginBottom: 8, color: "rgba(0,0,0,0.45)" }}>
        {`${comments.length} ${comments.length > 1 ? "replies" : "reply"}`}
      </div>
      {parentComments.map((comment) => (
        <CommentRenderer
          key={comment.cid}
          comment={comment}
          comments={comments}
          userId={userId}
          onReply={handleReply}
          setReplyPopoverCid={setReplyPopoverCid}
          replyPopoverCid={replyPopoverCid}
          newCommentBody={newCommentBody}
          setNewCommentBody={setNewCommentBody}
          handleReply={handleReply}
          onEdit={handleMenuClick}
          setEditPopoverCid={setEditPopoverCid}
          handleEditConfirm={handleEditConfirm}
          editPopoverCid={editPopoverCid}
          onDelete={handleDeleteConfirm}
          setDeletePopoverCid={setDeletePopoverCid}
          deleteDateLoading={deleteDateLoading}
          expandedReplies={expandedReplies}
          toggleReplies={toggleReplies}
        />
      ))}
      {/* {replyPopoverCid !== null && (
        //custom modal for replying to comments, since antd popover doesn't support controlled inputs
        <div className="custom-modal-wrapper">
          <div className="custom-modal-wrapper__header">
            Reply to Comment
            <button onClick={() => setReplyPopoverCid(null)} className="custom-modal-wrapper__header__close">
              <CloseOutlined />
            </button>
          </div>
          <div className="custom-modal-wrapper__content"></div>
          <Input.TextArea value={newCommentBody} onChange={(e) => setNewCommentBody(e.target.value)}></Input.TextArea>
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16, gap: 8 }}>
            <Button onClick={() => setReplyPopoverCid(null)}>Cancel</Button>
            <Button onClick={() => handleReply(replyPopoverCid)} type="primary">
              Save
            </Button>
          </div>
        </div>
      )} */}
    </>
  );
};

export default CommentList;
